import { inject, Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { EMPTY, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";

import { AWSHttpClient } from "@intocare/aws";
import { Pharmacy } from "@intocare/medication/medication-data";
import { SnackbarService } from "@intocare/ui";
import { clean } from "@intocare/util";

import { AddPharmacy, LoadingPharmacies, SetPharmacies, UpdatePharmacy } from "../actions/pharmacies.data";
import * as reducer from "../reducers";

@Injectable()
export class PharmaciesModel {
	private store = inject<Store<reducer.State>>(Store);
	private http = inject(AWSHttpClient);
	private snackbar = inject(SnackbarService);

	private getPharmacies$ = this.store.select(reducer.getPharmacies).pipe(
		switchMap((pharmacies) => {
			if (!pharmacies.loaded && !pharmacies.loading) {
				this.loadPharmacies();

				return EMPTY;
			}

			return of(pharmacies);
		}),
	);

	pharmacies$ = this.getPharmacies$.pipe(map((pharmacies) => pharmacies.data));
	loadingPharmacies$ = this.getPharmacies$.pipe(map((pharmacies) => pharmacies.loading));
	savingPharmacy$ = this.store.select(reducer.getSavingPharmacy);

	loadPharmacies() {
		this.store.dispatch(new LoadingPharmacies());
		this.http.get("/pharmacy").subscribe(
			(result: { pharmacies: Pharmacy[] }) => {
				this.store.dispatch(new SetPharmacies(result.pharmacies));
			},
			() => {
				this.store.dispatch(new SetPharmacies([]));
				this.snackbar.show({ text: "labels.homes.error.loadPharmacies", error: true });
			},
		);
	}

	addPharmacy(pharmacy: Pharmacy) {
		this.http.post("/pharmacy", clean(pharmacy)).subscribe(
			(result: { pharmacy: Pharmacy }) => {
				this.store.dispatch(new AddPharmacy(result.pharmacy));
				this.snackbar.show({ text: "labels.medication.pharmacy.created" });
			},
			(error: unknown) => {
				this.snackbar.show({ text: "labels.error.creating", error: true });
				console.log(error);
			},
		);
	}

	updatePharmacy(id: string, pharmacy: Pharmacy) {
		const pharm = {
			...pharmacy,
		};

		this.http.put(`/pharmacy/${id}`, clean(pharm)).subscribe(
			(result: { pharmacy: Pharmacy }) => {
				this.store.dispatch(new UpdatePharmacy(result.pharmacy));
				this.snackbar.show({ text: "labels.medication.pharmacy.updated" });
			},
			(error: unknown) => {
				console.log(error);
				this.snackbar.show({ text: "labels.error.saving", error: true });
			},
		);
	}
}
